/**
 * Pure state helpers for the picture-book create flow (/create-book with PICTURE_BOOK_COMIC_ENGINE on).
 * No React, no I/O: unit-tested in a node environment.
 */
import { READER_BAND_ORDER, inferReaderBand, type ReaderBand } from '@/utils/manga/comic-story-profile';
import type { SuggestionField } from '@/utils/analytics-events';
import type { PhotoScene } from '@/utils/picture-book/photo-scene';
import { previewInputKey } from '@/lib/comics/preview-session';
import { PICTURE_BOOK_STYLE_IDS, type PictureBookStyle } from '@/types/manga';
import type { MangaCharacterDraft, MangaPhoto } from '@/components/manga/types';
import { PICTURE_BOOK_FLOW_STYLES } from '@/utils/manga/picture-book-style';
import { pictureBookResumeTarget } from '@/lib/books/product-type';
import { COMIC_REDRAW_STALE_MS } from '@/components/manga/reader-state';
import { photoConsentJson } from '@/components/consent/photo-consent-gate';
import { BABY_ROLE_TEMPLATES, castAge, castForTemplate } from '@/lib/picture-book/casting';
import { pictureBookTemplate, templateIdeaFor } from '@/lib/picture-book/templates';
import type { FilledIdea } from '@/lib/catalogue/idea-cards';
import { guardCast, typedRelativesCovered, typedRelativesNotInPhoto, type TypedRelativesChoice } from '@/utils/manga/family-guard';

export type { SuggestionField };

export const PICTURE_BOOK_FLOW_STEPS = ['photo', 'people', 'your-book', 'preview', 'story'] as const;
export type PictureBookFlowStep = (typeof PICTURE_BOOK_FLOW_STEPS)[number];

/** Where a field's baseline value came from. An edit keeps the source, so Next can tell kept from edited. */
export type FieldSource = 'suggestion' | 'template' | 'parent';
export type FieldSources = Record<SuggestionField, FieldSource>;
export type PictureBookPageCount = 8 | 16 | 24;

export const SUGGESTION_FIELDS: readonly SuggestionField[] = ['style', 'title', 'idea'];

/** Older readers (O/W) get 24 pages, toddlers 8 where offered, everyone else 16. */
export function defaultPageCountForBand(band: ReaderBand, allowed: number[]): PictureBookPageCount {
  if ((band === 'O' || band === 'W') && allowed.includes(24)) return 24;
  if (band === 'T' && allowed.includes(8)) return 8;
  return 16;
}

type Details = { style: string; title: string; idea: string };

/** suggestion_kept / suggestion_edited per field whose baseline is the suggestion; equal after trimming = kept. */
export function suggestionEvents(
  suggested: Details | null,
  final: Details,
  source: Record<SuggestionField, FieldSource>,
): Array<{ name: 'suggestion_kept' | 'suggestion_edited'; field: SuggestionField }> {
  if (!suggested) return [];
  return SUGGESTION_FIELDS.filter((f) => source[f] === 'suggestion').map((field) => ({
    name: (suggested[field] ?? '').trim() === (final[field] ?? '').trim() ? 'suggestion_kept' : 'suggestion_edited',
    field,
  }));
}

/**
 * The preview-book key for the picture-book flow: people, photo, language and page count only. Style, title and
 * idea are left out on purpose, so changing them on an existing preview goes to the change route (a counted
 * redraw) instead of making a new book.
 */
export function pictureBookPreviewKey(i: { characters: unknown; photoDataUrl: string; language: string; pageCount: number }): string {
  return previewInputKey({
    characters: i.characters,
    photoDataUrl: i.photoDataUrl,
    language: i.language,
    storyTitle: '',
    storyIdea: '',
    settings: { flow: 'picture-book', pageCount: i.pageCount },
  });
}

/**
 * The title belongs to the parent (Shuffle must keep it): typed by the parent, or a suggestion the parent edited.
 * A template title does not: shuffling leaves the template, so its title goes too.
 */
export function titleIsParents(v: { title: string; source: FieldSource }, suggestedTitle: string | null | undefined): boolean {
  if (v.source === 'parent') return v.title.trim() !== '';
  if (v.source === 'suggestion') return suggestedTitle != null && v.title.trim() !== suggestedTitle.trim();
  return false;
}

/**
 * Rulings P2-R6 / P2-R10: the fallback pools and the ready-made stories are English. In a book that is not in English,
 * a title or idea still showing a fallback suggestion or a template's text needs the parent's edit or confirmation
 * before Next. (Editing template text makes it the parent's, so a 'template' source means unchanged.) Returns the
 * fields still to check.
 */
export function languageCheckFields(i: {
  suggestionSource: 'llm' | 'fallback' | undefined;
  suggested: { title: string; idea: string } | null | undefined;
  value: { title: string; idea: string; language: string; source: Record<SuggestionField, FieldSource> };
  confirmed: Partial<Record<'title' | 'idea', boolean>>;
  /** The language the confirmations were made in: they count only while the book is still in it. */
  confirmedLanguage?: string;
}): Array<'title' | 'idea'> {
  if (!i.value.language || i.value.language === 'en') return [];
  return (['title', 'idea'] as const).filter((f) => {
    if (i.confirmed[f] && i.confirmedLanguage === i.value.language) return false;
    if (i.value.source[f] === 'template') return true;
    return (
      i.value.source[f] === 'suggestion' &&
      i.suggestionSource === 'fallback' &&
      !!i.suggested &&
      i.value[f].trim() === i.suggested[f].trim()
    );
  });
}

/**
 * What the suggestions depend on: each person's name, age, gender, role, the photo's scene and the book language.
 * A change re-asks for the fields still showing an unedited suggestion (fieldsToSuggest), so a language switch never
 * leaves suggestion text in the old language; the parent's own text is never touched.
 */
export function suggestionPeopleKey(
  people: Array<{ name: string; age: string | number; gender: string; relationship: string; isMainCharacter: boolean }>,
  scene: PhotoScene | null | undefined,
  language = 'en',
): string {
  return JSON.stringify({
    l: language || 'en',
    p: people.map((c) => [c.name.trim(), String(c.age).trim(), c.gender, c.relationship.trim(), !!c.isMainCharacter]),
    s: scene ? [scene.setting, scene.season] : null,
  });
}

/**
 * Which text fields a (re)suggestion may fill: all of them on the first visit; after a people or scene change only
 * the ones still showing an unedited suggestion (a template or parent field, or an edited suggestion, is kept).
 */
export function fieldsToSuggest(v: {
  title: string;
  idea: string;
  source: Record<SuggestionField, FieldSource>;
  suggested?: { title: string; idea: string } | null;
}): Array<'title' | 'idea'> {
  // First visit: every field a suggestion may fill (a ?story= template's title and idea are never replaced)
  if (!v.suggested) return (['title', 'idea'] as const).filter((f) => v.source[f] !== 'template');
  const titleOwned = v.source.title === 'template' || titleIsParents({ title: v.title, source: v.source.title }, v.suggested.title);
  const ideaOwned = v.source.idea !== 'suggestion' || v.idea.trim() !== v.suggested.idea.trim();
  return [...(titleOwned ? [] : ['title' as const]), ...(ideaOwned ? [] : ['idea' as const])];
}

/** Which fields to (re)suggest now: none while the suggestion still matches the people, scene and language. */
export function resuggestFields(
  v: Parameters<typeof fieldsToSuggest>[0] & { suggestionFor?: string },
  currentKey: string,
): Array<'title' | 'idea'> {
  return v.suggestionFor === currentKey ? [] : fieldsToSuggest(v);
}

/** The parent picked a style: it is theirs from now on, so an arriving suggestion never replaces it. */
export function withParentStyle<T extends { style: string; source: Record<SuggestionField, FieldSource> }>(v: T, style: T['style']): T {
  return { ...v, style, source: { ...v.source, style: 'parent' } };
}

/** Whether an arriving suggestion may set the style: only while the style is still the (unedited) suggestion. */
export function suggestionMaySetStyle(v: {
  style: string;
  source: Record<SuggestionField, FieldSource>;
  suggested?: { style: string } | null;
}): boolean {
  if (v.source.style !== 'suggestion') return false;
  return !v.suggested || v.style === v.suggested.style;
}

// ---------- Task 11: preview (redraws, reading level), story, resume ----------

/** The most cover redraws a book gets (the server's PICTURE_BOOK_MAX_COVER_REDRAWS). */
export const MAX_COVER_REDRAWS = 3;

/** Owner decision FLAG 1 (2026-10-26): after any page edit the book's style, title, idea and reading level are fixed. */
export const EDITED_MESSAGE =
  "You've edited your story, so the style, title, idea and reading level are now fixed. To change them, start a new book.";
/** Owner decision FLAG 2 (2026-10-26): shown when the closest legal match changed the title or idea. */
export const LEGAL_MATCH_NOTICE =
  "We adjusted your story a little: we can't use characters, names or costumes that belong to other companies (copyright).";
export const REDRAW_LIMIT_MESSAGE = 'To change the cover again, please start a new book.';
/** P2-R9: only a paid or in-process order (or a bought book) fixes the story; the server decides. */
export const NOT_EDITABLE_MESSAGE = "This book has been ordered, so its story can't be changed any more.";
/** The 5-rewrite cap on reading-level changes (kind 'rewrite' on the 429), not the cover-redraw cap. */
export const REWRITE_LIMIT_MESSAGE =
  'You have changed the reading level as many times as we allow for one book. To try another, please start a new book.';
export const NOT_CHANGEABLE_MESSAGE = "This book can't be changed any more (it may already be ordered).";

const pbMeta = (book: any): Record<string, any> => {
  const pb = book?.metadata?.picture_book;
  return pb && typeof pb === 'object' ? pb : {};
};
/** A ready-made catalogue story keeps its own title: shown under the read-only title field. */
export const FIXED_TITLE_NOTE = "This ready-made story keeps its own title.";
/** A book stamped with a catalogue master: its title is the catalogue title and cannot be changed. */
export const pictureBookTitleFixed = (book: any): boolean => typeof pbMeta(book).master?.id === 'string';
const settingsOf = (book: any): Record<string, any> => book?.metadata?.manga?.settings ?? book?.metadata?.manga_settings ?? {};

/**
 * A DRAFT picture book whose cover redraw died (metadata.picture_book.redrawPending older than the server's stale
 * window, the comic one; an unreadable time counts as stale): the poll asks the change route once to put it back.
 */
export function pictureBookRedrawStuck(book: any, now: number = Date.now()): boolean {
  const pending = pbMeta(book).redrawPending;
  if (!book || book.status !== 'DRAFT' || !pending) return false;
  const at = Date.parse(String(pending.at ?? ''));
  return !Number.isFinite(at) || now - at > COMIC_REDRAW_STALE_MS;
}

/** Cover redraws still allowed: 3 minus metadata.picture_book.coverRedraws, never below 0. */
export function redrawsLeft(book: any): number {
  return Math.max(0, MAX_COVER_REDRAWS - (Number(pbMeta(book).coverRedraws) || 0));
}

const olderArt = (b: ReaderBand) => b === 'O' || b === 'W';

/** A reading-level change into or out of O/W switches the art and so redraws the cover; any other only rewrites. */
export function bandChangeIsRedraw(from: ReaderBand, to: ReaderBand): boolean {
  return olderArt(from) !== olderArt(to);
}

/** The confirmation before a band change that redraws the cover. */
export function redrawConfirmText(to: ReaderBand, left: number): string {
  return `This switches to art for ${olderArt(to) ? 'older' : 'younger'} readers and redraws the cover (${left} left). ${PICTURE_BOOK_COVER_GONE_WARNING}`;
}

/** Shown before any picture-book cover redraw: the cover on screen is gone for good once redrawn. */
export const PICTURE_BOOK_COVER_GONE_WARNING = "The cover you see now will be replaced and can't be brought back.";

/** Confirm text before a style/title/idea change redraws the cover. */
export function changeConfirmText(left: number): string {
  return `This redraws the cover with your changes. ${PICTURE_BOOK_COVER_GONE_WARNING} You have ${left} redraw${left === 1 ? '' : 's'} left.`;
}

/**
 * Your book -> Next with a preview book already made: the same preview key (people, photo, language, page count)
 * sends style/title/idea to the change route (a counted redraw); a new key makes a new book (review focus 2).
 */
export function routeDetailsChange(existing: { key: string; bookId: string } | null, nextKey: string): 'new-book' | 'change-route' {
  return existing && existing.bookId && existing.key === nextKey ? 'change-route' : 'new-book';
}

/** The book's reading band, read like the server's change route: stamp, settings, then inferred from the people. */
export function pictureBookBand(book: any): ReaderBand {
  const s = settingsOf(book);
  const found = [s.pictureBook?.band, s.readerBand, pbMeta(book).band].find((b) => READER_BAND_ORDER.includes(b));
  return (found as ReaderBand | undefined) ?? inferReaderBand(Array.isArray(book?.metadata?.characters) ? book.metadata.characters : []);
}

/** FLAG 2: the fields the closest legal match changed (empty unless metadata.picture_book.legalMatch.changed). */
export function legalNoticeFields(book: any): Array<'title' | 'idea'> {
  const m = pbMeta(book).legalMatch;
  if (!m || m.changed !== true || !Array.isArray(m.fields)) return [];
  return (['title', 'idea'] as const).filter((f) => m.fields.includes(f));
}

export type BookDetails = { style: PictureBookStyle; title: string; idea: string };

const firstString = (...v: unknown[]): string => (v.find((x) => typeof x === 'string') as string | undefined) ?? '';

/**
 * The style, title and idea the book's cover was made from, to pre-fill the change panel. When the legal match
 * changed the text, its text (what the cover shows; the server treats sending it back as no change).
 */
export function currentDetails(book: any, fallback?: Partial<BookDetails>): BookDetails {
  const pb = pbMeta(book);
  const style = [pb.style, settingsOf(book).pictureBook?.style, fallback?.style].find((s) =>
    (PICTURE_BOOK_STYLE_IDS as readonly unknown[]).includes(s),
  ) as PictureBookStyle | undefined;
  const legal = pb.legal?.text && typeof pb.legal.text === 'object' ? pb.legal.text : null;
  const request = book?.metadata?.manga_request ?? {};
  const legalChanged = pb.legalMatch?.changed === true && !!legal;
  return {
    style: style ?? 'watercolor',
    title: legalChanged
      ? firstString(legal.title, pb.title, book?.title)
      : pictureBookTitleFixed(book)
        // A master book: the book row holds the catalogue title (the stored request may be the parent's text)
        ? firstString(book?.title, pb.title, request.storyTitle, fallback?.title)
        : firstString(pb.title, book?.title, request.storyTitle, fallback?.title),
    idea: legalChanged ? firstString(legal.idea, pb.idea) : firstString(pb.idea, request.storyIdea, fallback?.idea),
  };
}

/** The style/title/idea fields that differ (after trimming): the change to send, or {} for none. */
export function detailsChangeFields(was: BookDetails, now: BookDetails): Partial<BookDetails> {
  const out: Partial<BookDetails> = {};
  if (now.style !== was.style) out.style = now.style;
  if (now.title.trim() !== was.title.trim()) out.title = now.title.trim();
  if (now.idea.trim() !== was.idea.trim()) out.idea = now.idea.trim();
  return out;
}

/** The gentle note under an edited page for a hint from PUT picture-book-story; null for kinds not shown. */
export function editHintText(h: { kind: string; detail: string }): string | null {
  switch (h.kind) {
    case 'registry-key':
      return `“${h.detail}” looks like a label, not a word a child would say`;
    case 'unknown-name':
      return `“${h.detail}” isn't anyone in this story`;
    case 'latin-name':
      return `Write ${h.detail} in the book's alphabet`;
    case 'emoji':
      return h.detail;
    default:
      return null;
  }
}

/** Carry an emailed link's ?bt= access token onto a book URL (lib/auth/book-access.ts accepts it on every book route). */
export function withBookToken(url: string, token: string | null | undefined): string {
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}bt=${encodeURIComponent(token)}`;
}

export type ChangeFailureKind = 'edited' | 'limit' | 'rewrite-limit' | 'not-changeable' | 'error';

/** What a refused POST picture-book-change means for the parent (a 409 busy is retried before this). */
export function changeFailure(status: number, body: any): { kind: ChangeFailureKind; message: string } {
  if (body?.error === 'edited') return { kind: 'edited', message: EDITED_MESSAGE };
  if ((status === 429 || body?.error === 'limit') && body?.kind === 'rewrite') return { kind: 'rewrite-limit', message: REWRITE_LIMIT_MESSAGE };
  if (status === 429 || body?.error === 'limit') return { kind: 'limit', message: REDRAW_LIMIT_MESSAGE };
  if (body?.error === 'not-changeable') return { kind: 'not-changeable', message: NOT_CHANGEABLE_MESSAGE };
  const text = status === 400 && typeof body?.error === 'string' ? body.error.trim() : '';
  return { kind: 'error', message: text || 'We could not change that. Please try again.' };
}

/**
 * Our own text in the idea field, which never triggers the typed-relative notice (Task C3): the ready-made story's
 * idea while a template is picked (the server uses the same baseline), else the suggestion while the idea is one.
 */
export function pictureBookIdeaBaseline(d: {
  templateId?: string;
  source?: { idea?: FieldSource };
  suggested?: { idea?: string } | null;
}): string | undefined {
  const tpl = d.templateId ? pictureBookTemplate(d.templateId) : undefined;
  if (tpl) return templateIdeaFor(tpl);
  if (d.source?.idea === 'suggestion' && d.suggested?.idea) return d.suggested.idea;
  return undefined;
}

/** The relatives the parent's idea names who are not in the photo (display words; empty when none). */
export function pictureBookTypedRelatives(
  characters: MangaCharacterDraft[],
  d: { idea: string; templateId?: string; source?: { idea?: FieldSource }; suggested?: { idea?: string } | null },
): string[] {
  const baseline = pictureBookIdeaBaseline(d);
  return typedRelativesNotInPhoto(d.idea, guardCast(characters), baseline ? { baseline } : {});
}

/** The body POST /api/books/create-picture-book-preview takes, from the flow's state. Pure. */
export function buildPictureBookPreviewPayload(i: {
  characters: MangaCharacterDraft[];
  details: {
    style: PictureBookStyle; title: string; idea: string; pageCount: PictureBookPageCount; language: string; templateId?: string;
    source?: { idea?: FieldSource }; suggested?: { idea?: string } | null;
    /** The parent's typed-relative choice: sent only when it covers the idea's current words (Task C3). */
    typedRelatives?: TypedRelativesChoice | null;
  };
  photo: MangaPhoto;
}) {
  const relativeWords = pictureBookTypedRelatives(i.characters, i.details);
  const sendChoice = relativeWords.length > 0 && typedRelativesCovered(relativeWords, i.details.typedRelatives);
  // A baby-role story (new-sibling): the child becoming the big sibling is main, whatever the people step marked
  // (the server recasts too); other templates keep the parent's choice. A cast that does not fit is sent as is.
  const people = castForTemplate(i.details.templateId, i.characters) ?? i.characters;
  // Age 0 / under 1 passes through only for a baby-role story; every other book keeps the old rule exactly
  const babyStory = !!i.details.templateId && BABY_ROLE_TEMPLATES.has(i.details.templateId);
  return {
    characters: people.map((c) => ({
      name: c.name.trim(),
      age: babyStory ? castAge(c) : Number(c.age) || 8,
      gender: c.gender,
      relationship: c.isMainCharacter ? undefined : c.relationship.trim() || undefined,
      isMainCharacter: c.isMainCharacter,
      faceCoordinates: c.faceCoordinates || undefined,
      // A head covering from the photo analysis (the server re-checks it against its allow-list)
      ...(c.headCovering ? { headCovering: c.headCovering } : {}),
    })),
    storyTitle: i.details.title.trim(),
    storyIdea: i.details.idea.trim() || undefined,
    style: i.details.style,
    pageCount: i.details.pageCount,
    language: i.details.language || 'en',
    band: inferReaderBand(i.characters),
    ...(i.details.templateId ? { templateId: i.details.templateId } : {}),
    photoBase64: i.photo.dataUrl,
    photoDimensions: i.photo.dimensions,
    // The tick (R3). A free-cover photo with no tick sends none: the route inherits the cover's consent (R5).
    ...photoConsentJson(i.photo.photoConsentVersion),
    ...(sendChoice ? { typedRelatives: { words: relativeWords, choice: 'continue' as const } } : {}),
  };
}

// ---------- Task 11 fix round 1 ----------

/**
 * The style/title/idea the PARENT asked for on this book (the Your book baseline): the text of the last change, else
 * the first request (metadata.picture_book.requested), never the legal-match rewrite. Compared with the Your book form
 * so a stored book gets the parent's real edits, and an unchanged form sends nothing (no wasted redraw).
 */
export function parentDetails(book: any): BookDetails {
  const pb = pbMeta(book);
  const requested = pb.requested && typeof pb.requested === 'object' ? pb.requested : {};
  return {
    style: currentDetails(book).style,
    title: firstString(pb.title, requested.title, book?.title),
    idea: firstString(pb.idea, requested.idea),
  };
}

/**
 * Whether a preview book found under the same key in this tab's sessionStorage may be reused. Never one that is gone
 * or no longer awaiting purchase (or not stamped). One whose story the parent edited (FLAG 1) or with no redraws left
 * cannot take a style/title/idea change, so with a change it is a new book; with no change it is reused as it is.
 */
export function storedBookDecision(i: { book: any; edited: boolean; change: Partial<BookDetails> }): 'reuse' | 'new-book' {
  const target = pictureBookResumeTarget(i.book);
  if (!i.book || (target !== 'flow-preview' && target !== 'flow-story')) return 'new-book';
  const changing = Object.keys(i.change).length > 0;
  if (changing && (i.edited || redrawsLeft(i.book) <= 0)) return 'new-book';
  return 'reuse';
}

/** A GET as the resolver sees it: the HTTP status (0 for a network failure) and the parsed body when ok. */
export type FetchedJson = { status: number; body?: any };
export type StoredBookResolution =
  | { kind: 'reuse'; details: BookDetails; edited: boolean }
  | { kind: 'new-book' }
  /** A transient failure (5xx, network): keep the stored entry, make nothing, let the parent try again. */
  | { kind: 'retry' };

export const STORED_BOOK_RETRY_MESSAGE = "We couldn't open your book — try again";

const gone = (status: number) => status === 403 || status === 404;

/**
 * What to do with a preview book found again under the same key. Only a 403/404 or an explicit unusable state makes a
 * new (paid) preview; anything transient is 'retry'. A written story is always read for its `edited` flags, so a reused
 * book carries its own FLAG 1 lock (never the previous book's).
 */
export async function resolveStoredBook(
  deps: { getBook: () => Promise<FetchedJson>; getStory: () => Promise<FetchedJson> },
  form: BookDetails,
): Promise<StoredBookResolution> {
  const safe = async (f: () => Promise<FetchedJson>): Promise<FetchedJson> => {
    try {
      return await f();
    } catch {
      return { status: 0 };
    }
  };
  const b = await safe(deps.getBook);
  if (gone(b.status)) return { kind: 'new-book' };
  if (b.status < 200 || b.status >= 300) return { kind: 'retry' };
  const book = b.body?.book ?? null;
  if (!book) return { kind: 'retry' };
  const baseline = parentDetails(book);
  const change = detailsChangeFields(baseline, form);
  let edited = false;
  if (pictureBookResumeTarget(book) === 'flow-story') {
    const s = await safe(deps.getStory);
    if (gone(s.status)) return { kind: 'new-book' };
    if (s.status < 200 || s.status >= 300) return { kind: 'retry' };
    edited = Array.isArray(s.body?.pages) && s.body.pages.some((p: any) => !!p?.edited);
  }
  return storedBookDecision({ book, edited, change }) === 'reuse' ? { kind: 'reuse', details: baseline, edited } : { kind: 'new-book' };
}

/** sessionStorage with key enumeration (the browser Storage). */
export interface EnumerableStorage {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  removeItem(key: string): void;
}

/** Start a new book: forget every stored preview key that points at this book (a resumed book has no key of its own). */
export function clearStoredEntriesForBook(storage: EnumerableStorage | null | undefined, bookId: string): void {
  if (!storage || !bookId) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith('joonoo:comic-preview:') && storage.getItem(k) === bookId) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
  } catch {
    // blocked storage: nothing stored to forget
  }
}

/** The story step stops polling after this long and offers Try again (each GET may start the page script). */
export const STORY_POLL_DEADLINE_MS = 8 * 60 * 1000;
export const STORY_SLOW_MESSAGE = 'This is taking longer than expected.';

/** Whether a story poll that started at `startedAt` has run out of time. */
export function storyPollExpired(startedAt: number, now: number): boolean {
  return now - startedAt >= STORY_POLL_DEADLINE_MS;
}

/** The preview reassures the parent after this long (the preview rule is ~60 s; the hard timeout is 6 min). */
export const PREVIEW_SLOW_MS = 60_000;
export const PREVIEW_SLOW_MESSAGE = 'Taking a little longer than usual — almost there…';

/** The dedication counter: UTF-16 units, the same unit as the input's maxLength. */
export function dedicationCount(value: string): number {
  return value.length;
}

/** The checkout URL for a picture book. The bt token goes along; the checkout page forwards it to its book load. */
export function checkoutHref(bookId: string, token: string | null | undefined): string {
  return withBookToken(`/checkout?bookId=${encodeURIComponent(bookId)}&flow=picture-book`, token);
}

/** ?style= on /create-book: a style the flow offers, else undefined. */
export function styleFromParam(raw: string | null | undefined): PictureBookStyle | undefined {
  const v = (raw ?? '').trim();
  return (PICTURE_BOOK_FLOW_STYLES as readonly string[]).includes(v) ? (v as PictureBookStyle) : undefined;
}

/** Seed the Your book style from ?style= as the parent's own choice (a suggestion never replaces it). */
export function seedParentStyle<T extends { style: string; source: Record<SuggestionField, FieldSource> }>(v: T, raw: string | null | undefined): T {
  const style = styleFromParam(raw);
  return style ? withParentStyle(v, style as T['style']) : v;
}

/** ?story= on /create-book: the id of a story template the flow offers, else undefined (comic and classic ids too). */
export function storyFromParam(raw: string | null | undefined): string | undefined {
  const v = (raw ?? '').trim();
  return v && pictureBookTemplate(v) ? v : undefined;
}

/**
 * Seed Your book with the ?story= template, as if the parent picked it: its idea, title left '' (the Details step's
 * people effect writes the title personalised for the people via templateTitleFor, so no [[NAME]] ever shows), and
 * template sources so no suggestion replaces either. An unknown id leaves the value unchanged.
 */
export function seedParentStory<T extends {
  title: string;
  idea: string;
  templateId?: string;
  source: Record<SuggestionField, FieldSource>;
  confirmed?: Partial<Record<'title' | 'idea', boolean>>;
}>(v: T, raw: string | null | undefined): T {
  const id = storyFromParam(raw);
  const t = id ? pictureBookTemplate(id) : undefined;
  if (!t) return v;
  return { ...v, templateId: t.id, title: '', idea: templateIdeaFor(t), source: { ...v.source, title: 'template', idea: 'template' }, confirmed: {} };
}

// ---------- Make up your own story (Task 5): idea cards on Your book ----------

/** The Your book fields an idea card touches (PictureBookDetailsStep's DetailsValue has them all). */
export interface CardDetailsFields {
  style: PictureBookStyle;
  title: string;
  idea: string;
  templateId?: string;
  source: Record<SuggestionField, FieldSource>;
  suggested?: { style: PictureBookStyle; title: string; idea: string } | null;
  suggestionSource?: 'llm' | 'fallback';
  confirmed?: Partial<Record<'title' | 'idea', boolean>>;
  /** The idea card that filled the title and idea (unset by Surprise me, a ready-made story or an AI suggestion). */
  ideaCard?: string;
}

/**
 * A tapped idea card: title and idea together, as a suggestion (so suggestion_kept/edited and the English language
 * check, P2-R6, work unchanged: the cards are English like the fallback pool). Leaves any ready-made story.
 */
export function cardDetails<T extends CardDetailsFields>(v: T, card: FilledIdea): T {
  return {
    ...v,
    title: card.title,
    idea: card.idea,
    templateId: undefined,
    source: { ...v.source, title: 'suggestion', idea: 'suggestion' },
    suggested: { style: v.suggested?.style ?? v.style, title: card.title, idea: card.idea },
    suggestionSource: 'fallback',
    ideaCard: card.id,
    confirmed: {},
  };
}

/**
 * The people or the language changed: an unedited card is filled again with the new names (no AI call). When only
 * the title is the parent's, the idea alone is refilled and the title (and its confirmation) kept. Null when there
 * is no card, the idea was edited, or the card is no longer offered (`card` undefined): the caller re-asks.
 */
export function refillFromCard<T extends CardDetailsFields>(v: T, card: FilledIdea | undefined): T | null {
  if (!v.ideaCard || !v.suggested || !card || card.id !== v.ideaCard) return null;
  if (v.source.idea !== 'suggestion' || v.idea !== v.suggested.idea) return null;
  const titleUnedited = v.source.title === 'suggestion' && v.title === v.suggested.title;
  if (titleUnedited) return cardDetails(v, card);
  return {
    ...v,
    idea: card.idea,
    suggested: { ...v.suggested, idea: card.idea },
    ideaCard: card.id,
    confirmed: { title: v.confirmed?.title },
  };
}

/**
 * `idea-card:<id>` for analytics while the text came from a card. When the title and idea are given, both rewritten
 * means the card no longer shaped the book, so no source.
 */
export function ideaSourceOf(v: {
  ideaCard?: string;
  title?: string;
  idea?: string;
  suggested?: { title: string; idea: string } | null;
}): string | undefined {
  if (!v.ideaCard) return undefined;
  if (v.suggested && v.title !== undefined && v.idea !== undefined && v.title !== v.suggested.title && v.idea !== v.suggested.idea) {
    return undefined;
  }
  return `idea-card:${v.ideaCard}`;
}

/**
 * The key suggestion_kept/edited were last sent for: Back then Next with nothing changed sends them only once.
 * Null when there is nothing to send (no suggestion).
 */
export function suggestionEventsKey(v: { style: string; title: string; idea: string; suggested?: { style: string; title: string; idea: string } | null; ideaCard?: string }): string | null {
  if (!v.suggested) return null;
  return JSON.stringify([v.ideaCard ?? null, v.suggested.style, v.suggested.title, v.suggested.idea, v.style, v.title, v.idea]);
}
